import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { THEME_COLORS } from "../../context/constants";
import { AvatarGroup, HeygenVoice } from "../../context/types";
import HeygenVoicesDialog from "../../components/VoicePlayer/components/HeygenVoicesDialog";

interface EditGroupVoiceDialogProps {
  open: boolean;
  group: AvatarGroup | null;
  voices: HeygenVoice[];
  voicesLoading: boolean;
  onClose: () => void;
  onSave: (payload: { default_voice_id: string | null }) => Promise<void>;
}

const EditGroupVoiceDialog: React.FC<EditGroupVoiceDialogProps> = ({
  open,
  group,
  voices,
  voicesLoading,
  onClose,
  onSave,
}) => {
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [voiceMeta, setVoiceMeta] = useState<{ name: string } | null>(null);
  const [voicesDialogOpen, setVoicesDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && group) {
      setVoiceId(group.default_voice_id ?? null);
      setVoiceMeta(null);
    }
  }, [open, group]);

  const selectedVoiceName = useMemo(() => {
    if (voiceMeta?.name) return voiceMeta.name;
    if (!voiceId) return null;
    const match = voices.find((v) => v.voice_id === voiceId);
    return match?.name ?? null;
  }, [voiceId, voiceMeta, voices]);

  const handleSelectVoice = (
    selectedVoiceId: string,
    meta?: { name: string; preview_audio_url: string | null }
  ) => {
    setVoiceId(selectedVoiceId);
    if (meta?.name) setVoiceMeta({ name: meta.name });
    setVoicesDialogOpen(false);
  };

  const handleSave = async () => {
    if (!group) return;
    try {
      setIsSaving(true);
      await onSave({ default_voice_id: voiceId });
    } finally {
      setIsSaving(false);
    }
  };

  const lookCount = group?.looks?.length ?? 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            fontFamily: "Lexend, sans-serif",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Set default voice for group
          <IconButton onClick={onClose} sx={{ marginLeft: "auto" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: "8px !important",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              color: "#666",
              fontFamily: "Lexend, sans-serif",
            }}
          >
            This voice will be applied to all{" "}
            <strong>{lookCount}</strong> look{lookCount === 1 ? "" : "s"} in{" "}
            <strong>{group?.name}</strong> and used by default when generating
            videos with any of them.
          </Typography>

          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#666",
                fontFamily: "Lexend, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "4px",
              }}
            >
              Default voice
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<RecordVoiceOverIcon />}
                onClick={() => setVoicesDialogOpen(true)}
                disabled={voicesLoading}
                sx={{
                  textTransform: "none",
                  fontFamily: "Lexend, sans-serif",
                  borderRadius: "10px",
                  borderColor: "#ddd",
                  color: "#333",
                }}
              >
                {voicesLoading ? "Loading voices..." : "Choose voice"}
              </Button>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: selectedVoiceName ? "#222" : "#999",
                  fontFamily: "Lexend, sans-serif",
                }}
              >
                {selectedVoiceName
                  ? selectedVoiceName
                  : voiceId
                  ? `Voice id ${voiceId.slice(0, 8)}...`
                  : "No default voice selected"}
              </Typography>
              {voiceId && (
                <Button
                  size="small"
                  onClick={() => {
                    setVoiceId(null);
                    setVoiceMeta(null);
                  }}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Lexend, sans-serif",
                    color: "#999",
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px 20px" }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
            sx={{
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              fontFamily: "Lexend, sans-serif",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "8px 20px",
              fontSize: "14px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: THEME_COLORS.darkYellow,
                boxShadow: "none",
              },
            }}
            startIcon={
              isSaving ? <CircularProgress size={16} sx={{ color: "#000" }} /> : undefined
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <HeygenVoicesDialog
        open={voicesDialogOpen}
        onClose={() => setVoicesDialogOpen(false)}
        heygenVoices={voices}
        onSelectVoice={handleSelectVoice}
        actorId={group?._id}
        actorName={group?.name}
      />
    </>
  );
};

export default EditGroupVoiceDialog;
